import { z } from 'zod';

export const conversationStatus = z.enum([
  'Processing',
  'Done',
  'Error',
  'Cancel',
  'Fatal'
]);
export const taskStatus = z.enum([
  'OK',
  'Error',
  'No-Content',
  'Cancel',
  'Fatal',
]);
export const accessLevel = z.enum(['public', 'private']);

export const cancelStepContent = z.enum(['REQUEST_CANCELLED']);
export const errorStepContent = z.enum([
  'MAX_ITERATIONS',
  'SERVICE_BUSY',
  'UNSPECIFIED',
]);
export const fatalStepContent = z.enum(['CONTEXT_LIMIT']);

export const analysisMode = z.enum(['Quick', 'Deep']);

export const fatalContentValues = fatalStepContent.options;

export const allFailedContentValues = [
  ...cancelStepContent.options,
  ...errorStepContent.options,
  ...fatalStepContent.options,
];

export const step = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  start_time: z.string(),
  duration_seconds: z.number(),
  next: z.string(),
  status: taskStatus,
  content_type: z.string(),
  content: z.string(),
  analysis_mode: analysisMode.nullish(),
});

const conversationMessageResponseSchema = z.object({
  content: z.string().optional(),
  status: taskStatus,
});

const postConversationSchema = z.object({
  conversation_id: z.string(),
  request_id: z.string(),
  name: z.string(),
});

const continueConversationSchema = z.object({ request_id: z.string() });

export const conversationMessagesSchema = z.object({
  states: z.array(step),
});

const conversationMessage = z.object({
  request_id: z.string(),
  start_time: z.string(),
  duration_seconds: z.number().optional(),
  input: z.string(),
  output: z.string().optional(),
  first_state: step.optional(),
  last_state: step.optional(),
  analysis_mode: analysisMode.nullish(),
});

const conversationInfo = z.object({
  name: z.string(),
  date: z.string(), // updated_date, it is when the last question was asked (or when the conversation was copied).
  id: z.string(),
  summary: z.string(),
  favourite: z.boolean(),
  access_level: accessLevel,
  created_date: z.string(),
  status: conversationStatus.nullish(),
});

const getConversationTasksSchema = z.object({
  ...conversationInfo.shape,
  tasks: z.array(conversationMessage),
});

const conversationSearchResult = z.object({
  name: z.string(),
  conversation_id: z.string(),
  request_id: z.string(),
  updated_at: z.string(),
  state: step,
});

const searchConversationSchema = z.object({
  results: z.array(conversationSearchResult),
});

const conversationSchema = z.object({
  ...conversationInfo.shape,
  public_copies: z.array(conversationInfo),
});

const getAllConversationsSchema = z.object({
  conversations: z.array(conversationSchema),
});

export const feedbackEnum = z.enum(['Good', 'Bad', 'Neutral']);
const postConversationFeedback = z.object({
  conversation_id: z.string(),
  request_id: z.string(),
  feedback: feedbackEnum,
});

const copyConversationSchema = z.object({
  conversation_id: z.string(),
  name: z.string(),
});

const settingsSchema = z.object({
  prompt: z.string(),
});

export type AccessLevel = z.infer<typeof accessLevel>;
export type CancelStepContent = z.infer<typeof cancelStepContent>;
export type ErrorStepContent = z.infer<typeof errorStepContent>;
export type FatalStepContent = z.infer<typeof fatalStepContent>;
export type AllFailedContentValues = typeof allFailedContentValues[number];
export type Step = z.infer<typeof step>;
export type ConversationMessageResponseSchema = z.infer<
  typeof conversationMessageResponseSchema
>;
export type ConversationMessagesSchema = z.infer<
  typeof conversationMessagesSchema
>;
export type PostConversationResponse = z.infer<typeof postConversationSchema>;
export type ContinueConversationResponse = z.infer<
  typeof continueConversationSchema
>;
export type BasedConversation = z.infer<typeof conversationSchema>; // base
export type ConversationSearchResult = z.infer<typeof conversationSearchResult>;
export type SearchConversationResponse = z.infer<
  typeof searchConversationSchema
>;
export type GetConversationTasksSchema = z.infer<
  typeof getConversationTasksSchema
>;
export type PostConversationFeedback = z.infer<typeof postConversationFeedback>;
export type ConversationMessageFeedback = z.infer<typeof feedbackEnum>;
export type ProcessingStatus = z.infer<typeof taskStatus>;
export type ConversationStatus = z.infer<typeof conversationStatus>;
export type Task = z.infer<typeof conversationMessage>;

export const conversations = {
  getAllConversations: {
    path: 'conversation/v2',
    schema: getAllConversationsSchema,
  },
  conversationId: {
    path: (id: string) => `conversation/v2/${id}`,
  },
  postConversation: { path: 'conversation/v2', schema: postConversationSchema },
  continueConversation: {
    path: (id: string) => `conversation/v2/${id}`,
    schema: continueConversationSchema,
  },
  getConversationTask: {
    path: (id: string) => `conversation/v2/${id}`,
    schema: getConversationTasksSchema,
  },
  searchConversation: {
    path: 'conversation/v2/search',
    schema: searchConversationSchema,
  },
  getConversationMessage: {
    path: (conversationId: string, requestId: string) =>
      `conversation/v2/${conversationId}/tasks/${requestId}`,
    schema: conversationMessagesSchema,
  },
  feedback: {
    path: 'conversation/v2/feedback',
    schema: postConversationFeedback,
  },
  abortConversation: {
    path: (id: string) => `conversation/v2/${id}/tasks`,
  },
  changeAccessLevel: {
    path: (id: string) => `conversation/v2/${id}/copy`,
    schema: copyConversationSchema,
  },
  getSettings: {
    path: `conversation/v2/settings/preferences.format`,
    schema: settingsSchema,
  },
  putSettings: {
    path: `conversation/v2/settings/preferences.format`,
  },
  quickDeep: { path: 'quick-deep/v1/analyze' },
};
